// EPIC-P05-07: Badge Form Component
// Create/Edit form for badges with validation

import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { BadgePreview } from './BadgePreview'
import { BadgeCriteriaEditor } from './BadgeCriteriaEditor'
import { createBadge, updateBadge, BadgeCreateInput, BadgeUpdateInput, BadgeDetail } from '@/lib/api/admin/badges'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

const badgeSchema = z.object({
  badge_key: z.string().min(1, 'Badge key is required').regex(/^[a-z0-9_]+$/, 'Badge key must be lowercase alphanumeric with underscores'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  category: z.enum(['analysis', 'practice', 'streak', 'milestone', 'social']),
  rarity: z.enum(['common', 'rare', 'epic', 'legendary']),
  criteria: z.any(),
  icon_path: z.string().nullable().optional(),
  is_active: z.boolean().default(true),
})

type BadgeFormValues = z.infer<typeof badgeSchema>

interface BadgeFormProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  badge?: BadgeDetail | null
}

export function BadgeForm({ open, onClose, onSuccess, badge }: BadgeFormProps) {
  const isEdit = !!badge
  const form = useForm<BadgeFormValues>({
    resolver: zodResolver(badgeSchema),
    defaultValues: {
      badge_key: '',
      title: '',
      description: '',
      category: 'social',
      rarity: 'common',
      criteria: {},
      icon_path: null,
      is_active: true,
    },
  })

  const watchedValues = form.watch()

  useEffect(() => {
    if (badge) {
      form.reset({
        badge_key: badge.badge_key,
        title: badge.title,
        description: badge.description,
        category: badge.category,
        rarity: badge.rarity,
        criteria: badge.criteria || {},
        icon_path: badge.icon_path,
        is_active: badge.is_active ?? true,
      })
    } else {
      form.reset({
        badge_key: '',
        title: '',
        description: '',
        category: 'social',
        rarity: 'common',
        criteria: {},
        icon_path: null,
        is_active: true,
      })
    }
  }, [badge, form])

  const onSubmit = async (values: BadgeFormValues) => {
    try {
      if (isEdit && badge) {
        const updateInput: BadgeUpdateInput = {
          id: badge.id,
          ...values,
        }
        await updateBadge(updateInput)
        toast.success('Badge updated successfully')
      } else {
        const createInput: BadgeCreateInput = values
        await createBadge(createInput)
        toast.success('Badge created successfully')
      }
      onSuccess()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save badge')
    }
  }

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEdit ? 'Edit Badge' : 'Create Badge'}</SheetTitle>
          <SheetDescription>
            {isEdit ? 'Update badge details and criteria' : 'Create a new badge with criteria and metadata'}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: Form Fields */}
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="badge_key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Badge Key</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="first_post"
                          disabled={isEdit}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Unique identifier (lowercase, alphanumeric, underscores only)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input placeholder="First Post" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Shared your first post in the Clubhouse"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="analysis">Analysis</SelectItem>
                            <SelectItem value="practice">Practice</SelectItem>
                            <SelectItem value="streak">Streak</SelectItem>
                            <SelectItem value="milestone">Milestone</SelectItem>
                            <SelectItem value="social">Social</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="rarity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rarity</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="common">Common</SelectItem>
                            <SelectItem value="rare">Rare</SelectItem>
                            <SelectItem value="epic">Epic</SelectItem>
                            <SelectItem value="legendary">Legendary</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="icon_path"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Icon Path (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="badges/social/id.webp"
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormDescription>
                        Path to badge image in Supabase Storage
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel>Active</FormLabel>
                        <FormDescription>
                          When inactive, badge is hidden from users
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="criteria"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Criteria</FormLabel>
                      <FormControl>
                        <BadgeCriteriaEditor
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Right Column: Preview */}
              <div className="lg:sticky lg:top-4">
                <BadgePreview
                  category={watchedValues.category}
                  rarity={watchedValues.rarity}
                  title={watchedValues.title}
                  description={watchedValues.description}
                  badge_key={watchedValues.badge_key}
                  icon_path={watchedValues.icon_path}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-4 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEdit ? 'Update Badge' : 'Create Badge'}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

